import React, { useEffect, useState } from 'react';
import { unifiedTasksApi } from '../../api/unifiedTasks';
import { laneApi } from '../../api/laneApi';
import { fetchClaims, Claim } from '../../api/claims';
import { Button, ToastMessage } from '../../components/ui';
import EmailComposer from '../../components/EmailComposer';
import { EmailAttachment } from '../../api/emails';
import { parseBackendTimestamp } from './aging';
import './lanes.css';

/**
 * "Forward to client" flow (Tasks Rebuild Task 12), extracted once and
 * reused by both `TaskDetailPane`'s single-task action and
 * `LenderLane`'s bulk bar (the only bulk bar that had a "Forward to client"
 * stub -- Post/Manual never did, see task-12-report.md).
 *
 * This is the same flow as the legacy Tasks.tsx's `handleReplyToEmail` +
 * `EmailComposer` modal: fetch the assigned claim, then open `EmailComposer`
 * prefilled with the original message quoted underneath. Only available
 * when the task has an `assignedClaimId` (matches the legacy gating -- see
 * Tasks.tsx line ~4038, "Reply to Email" only renders when a claim is
 * assigned) and has email content to quote.
 *
 * Given a task id (not the fully-loaded task/content -- so a bulk-bar caller
 * that only has a slim `LaneTaskRow` can use this exactly the same way the
 * detail pane does), this fetches what it needs itself and renders nothing
 * visible until ready, at which point it becomes the real `EmailComposer`
 * modal.
 */

export interface ForwardComposerProps {
  taskId: string;
  onClose: () => void;
  onSent?: () => void;
  showToast?: (toast: ToastMessage) => void;
}

function formatDateTime(iso: string | null | undefined): string {
  if (!iso) return '';
  const d = parseBackendTimestamp(iso);
  if (Number.isNaN(d.getTime())) return '';
  return d.toLocaleString('en-GB');
}

export const ForwardComposer: React.FC<ForwardComposerProps> = ({ taskId, onClose, onSent, showToast }) => {
  const [loading, setLoading] = useState(true);
  const [errorMsg, setErrorMsg] = useState<string | null>(null);
  const [claim, setClaim] = useState<Claim | null>(null);
  const [emailContent, setEmailContent] = useState<Record<string, any> | null>(null);
  const [taskSubject, setTaskSubject] = useState<string | null>(null);

  useEffect(() => {
    let cancelled = false;
    (async () => {
      try {
        const [task, content] = await Promise.all([
          unifiedTasksApi.getTask(taskId),
          laneApi.getTaskContent(taskId),
        ]);
        if (cancelled) return;
        if (!task.assignedClaimId) {
          const msg = 'This task has no assigned claim to reply on behalf of.';
          setErrorMsg(msg);
          showToast?.({ type: 'error', message: msg });
          setLoading(false);
          return;
        }
        const claims = await fetchClaims(1, 1, task.assignedClaimId);
        if (cancelled) return;
        if (!claims.items || claims.items.length === 0) {
          const msg = 'Could not find the assigned claim.';
          setErrorMsg(msg);
          showToast?.({ type: 'error', message: msg });
          setLoading(false);
          return;
        }
        setClaim(claims.items[0]);
        setEmailContent((content.emailContent as Record<string, any>) || null);
        setTaskSubject(task.subject || null);
        setLoading(false);
      } catch {
        if (!cancelled) {
          const msg = 'Failed to load task details.';
          setErrorMsg(msg);
          showToast?.({ type: 'error', message: msg });
          setLoading(false);
        }
      }
    })();
    return () => { cancelled = true; };
    // showToast is intentionally omitted: it's a prop that may not be stable
    // across parent re-renders, and this fetch must key only off taskId.
    // Calling it inside the effect (like onSent/onClose) is fine without listing it.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [taskId]);

  if (loading) {
    return (
      <div className="lane-modal-backdrop" onClick={onClose}>
        <div className="lane-modal" onClick={(e) => e.stopPropagation()}>
          <p className="lane-modal__hint">Loading claim details…</p>
        </div>
      </div>
    );
  }

  if (errorMsg || !claim) {
    return (
      <div className="lane-modal-backdrop" onClick={onClose}>
        <div className="lane-modal" onClick={(e) => e.stopPropagation()}>
          <div className="lane-modal__error">{errorMsg || 'Failed to load claim details.'}</div>
          <div className="lane-modal__actions">
            <Button variant="secondary" onClick={onClose}>Close</Button>
          </div>
        </div>
      </div>
    );
  }

  return (
    <EmailComposer
      claim={claim}
      taskId={taskId}
      defaultTo={claim.email || ''}
      warnIfRecipient={emailContent?.from || undefined}
      defaultSubject={`RE: ${emailContent?.subject || taskSubject || 'Your inquiry'}`}
      defaultBody={[
        '<br><br><hr><strong>Original Message:</strong><br>',
        `From: ${emailContent?.from || ''}<br>`,
        `Subject: ${emailContent?.subject || taskSubject || ''}<br>`,
        `Date: ${emailContent?.receivedDate ? formatDateTime(emailContent.receivedDate) : ''}<br><br>`,
        emailContent?.body || '',
      ].join('').trim()}
      initialAttachments={((emailContent?.attachments || []) as Record<string, any>[])
        .filter((attachment) => (
          !attachment.isInline && attachment.stored === true && Boolean(attachment.blobName)
        ))
        .map((attachment): EmailAttachment => ({
          name: attachment.name || attachment.filename || 'attachment',
          sourceTaskId: taskId,
          sourceBlobName: attachment.blobName,
        }))}
      onClose={onClose}
      onSent={() => {
        showToast?.({ type: 'success', message: 'Email sent.' });
        onSent?.();
      }}
    />
  );
};

export default ForwardComposer;
